import { useEffect, useId, useState } from "react";
import { X } from "lucide-react";
import { searchPeople, useOrg, type PersonLite } from "@vixio/data";
import { Button } from "@vixio/ui";

export interface PersonPickerProps {
  label: string;
  value: PersonLite | null;
  onChange: (p: PersonLite | null) => void;
  hint?: string;
  required?: boolean;
  error?: string | null;
}

/** Escolha de pessoa do cadastro único (core.people) por nome, e-mail ou empresa. */
export function PersonPicker({ label, value, onChange, hint, required, error }: PersonPickerProps) {
  const org = useOrg();
  const id = useId();
  const [text, setText] = useState("");
  const [results, setResults] = useState<PersonLite[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (text.trim().length < 2) {
      setResults([]);
      return;
    }
    let alive = true;
    const t = setTimeout(() => {
      searchPeople(org.id, text, 8)
        .then((r) => {
          if (!alive) return;
          setResults(r);
          setActive(0);
          setOpen(true);
        })
        .catch(() => alive && setResults([]));
    }, 200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [text, org.id]);

  function pick(p: PersonLite) {
    onChange(p);
    setText("");
    setOpen(false);
  }

  return (
    <div className="vx-field vx-combo">
      <label className="vx-field__label" htmlFor={id}>
        {label}
        {required ? <span className="req" aria-hidden="true">*</span> : null}
      </label>
      {value ? (
        <div className="vx-picked">
          <span className="vx-picked__text">
            <strong style={{ fontWeight: 500 }}>{value.full_name}</strong>
            {value.email ? <span className="muted"> · {value.email}</span> : null}
          </span>
          <Button size="sm" variant="ghost" iconOnly label="Trocar pessoa" icon={<X />} onClick={() => onChange(null)} />
        </div>
      ) : (
        <>
          <input
            id={id}
            className="vx-input"
            value={text}
            placeholder="Digite nome, e-mail ou empresa"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={open && results.length > 0}
            aria-controls={`${id}-list`}
            aria-activedescendant={open && results[active] ? `${id}-opt-${active}` : undefined}
            aria-invalid={error ? true : undefined}
            autoComplete="off"
            onChange={(e) => setText(e.target.value)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onKeyDown={(e) => {
              if (!open || results.length === 0) return;
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, results.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              } else if (e.key === "Enter") {
                e.preventDefault();
                pick(results[active]!);
              } else if (e.key === "Escape") setOpen(false);
            }}
          />
          {open && text.trim().length >= 2 ? (
            <ul id={`${id}-list`} role="listbox" className="vx-menu vx-combo__list">
              {results.length === 0 ? <li className="vx-menu__item" aria-disabled="true">Ninguém encontrado. Cadastre em Pessoas.</li> : null}
              {results.map((p, i) => (
                <li
                  key={p.id}
                  id={`${id}-opt-${i}`}
                  role="option"
                  aria-selected={i === active}
                  className="vx-menu__item"
                  data-highlighted={i === active ? "" : undefined}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(p);
                  }}
                >
                  <span>
                    {p.full_name}
                    <span className="muted" style={{ fontSize: "var(--fs-12)" }}>
                      {" "}
                      {p.email ?? p.company_name ?? ""}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
      {hint ? <span className="vx-field__hint">{hint}</span> : null}
      {error ? <span className="vx-field__error">{error}</span> : null}
    </div>
  );
}
